import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import Loading from '../Loading';

import config from '../../config';

import { publicRoutes } from '../../utils/constants';
import { isEmailVerificationRequired, redirectToVerifyEmail } from '../../utils/emailVerification';

// Account creation and player-profile (onboarding) creation are two separate steps. Without this
// guard the gap is only discovered by the API at the last step before payment (400 / 70008
// MemberDoesNotHavePlayerProfile), which leaves the member stranded. Leagues gates the same way the
// legacy consumer app does: send the member to the main app's onboarding flow and bring them back.
const REDIRECT_MARKER_KEY = 'onboardingRedirectAt';
export const REDIRECT_MARKER_MAX_AGE_MS = 10 * 60 * 1000;

const Resolution = {
    REDIRECTING: 'redirecting',
    NOTICE: 'notice',
    BYPASS: 'bypass',
};

// Deliberately NOT `!onboardingCompleted` alone. The legacy app's requireOnboarding HOC does that and
// it traps members who do not need onboarding at all in a redirect loop.
export const shouldRedirectToOnboarding = (user) => {
    if (!user) {
        return false;
    }

    return !!user.onboardingRequired && !user.onboardingCompleted;
};

export const buildOnboardingUrl = () => {
    return `${config.appHost}/onboarding?return=${encodeURIComponent(window.location.href)}`;
};

// sessionStorage throws in Safari private mode and in some embedded webviews, so every access is
// guarded. A storage failure degrades to "render children" — never to a crash or a redirect loop.
const readRedirectMarkerAgeMs = () => {
    try {
        const recordedAt = Number(window.sessionStorage.getItem(REDIRECT_MARKER_KEY));

        if (!Number.isFinite(recordedAt) || recordedAt <= 0) {
            return { isReadable: true, ageMs: null };
        }

        return { isReadable: true, ageMs: Date.now() - recordedAt };
    } catch (error) {
        return { isReadable: false, ageMs: null };
    }
};

const writeRedirectMarker = () => {
    try {
        window.sessionStorage.setItem(REDIRECT_MARKER_KEY, String(Date.now()));
        return true;
    } catch (error) {
        return false;
    }
};

const OnboardingGuard = ({ children }) => {
    const location = useLocation();
    // App.js aliases this slice's `isLoadingUser` to a `loading` key the slice never sets, so only
    // `isUserLoaded` is a trustworthy signal that getAuthUser has settled.
    const { user, isUserLoaded } = useSelector((state) => state.auth);
    const [resolution, setResolution] = useState(null);

    const isPublicRoute = publicRoutes.includes(location.pathname);
    // Email verification is checked before onboarding: a member who must verify cannot use the API
    // anyway, and onboarding happens after verification in the main app. No loop-breaker marker is
    // needed here -- the verify screen is a hard stop and verifying clears the flag.
    const isVerifyCandidate = !isPublicRoute && !!isUserLoaded && isEmailVerificationRequired(user);
    // Never an onboarding candidate while verification is pending, so no onboarding marker is
    // written and the two redirects cannot race.
    const isGuardCandidate =
        !isPublicRoute && !!isUserLoaded && !isVerifyCandidate && shouldRedirectToOnboarding(user);

    useEffect(() => {
        if (isVerifyCandidate) {
            redirectToVerifyEmail();
        }
    }, [isVerifyCandidate]);

    useEffect(() => {
        if (!isGuardCandidate) {
            setResolution(null);
            return;
        }

        const { isReadable, ageMs } = readRedirectMarkerAgeMs();

        if (!isReadable) {
            setResolution(Resolution.BYPASS);
            return;
        }

        if (ageMs !== null && ageMs < REDIRECT_MARKER_MAX_AGE_MS) {
            // We already sent this member to onboarding and they came back still incomplete.
            // Stop redirecting and let them choose, so we can never loop.
            //
            // The marker is deliberately NOT cleared here. Clearing it makes this branch
            // single-use: any later run of this effect -- a refetched member, a remount, a
            // second guarded route in the same session -- would find no marker and redirect
            // again, which is the loop this branch exists to stop. Verified on test2, where
            // four consecutive visits all redirected with a fresh marker present. Leaving the
            // marker in place keeps the decision idempotent; it ages out on its own.
            setResolution(Resolution.NOTICE);
            return;
        }

        if (!writeRedirectMarker()) {
            setResolution(Resolution.BYPASS);
            return;
        }

        setResolution(Resolution.REDIRECTING);
        window.location.href = buildOnboardingUrl();
    // Keyed on the candidacy flag rather than the pathname: once a member is a candidate the
    // redirect is already in flight, so guarded -> guarded SPA navigation must not re-run this.
    // Member id is included so a sign-out/sign-in as a different incomplete member inside one
    // session re-evaluates instead of inheriting the previous member's resolution.
    }, [isGuardCandidate, user?.id]);

    if (isVerifyCandidate) {
        return <Loading />;
    }

    if (!isGuardCandidate || resolution === Resolution.BYPASS) {
        return <>{children}</>;
    }

    if (resolution === Resolution.NOTICE) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    Finish setting up your profile to register
                </Typography>
                <Link href={buildOnboardingUrl()} underline="always">
                    Complete your profile
                </Link>
            </Box>
        );
    }

    return <Loading />;
};

OnboardingGuard.propTypes = {
    children: PropTypes.node,
};

OnboardingGuard.defaultProps = {
    children: null,
};

export default OnboardingGuard;
